/**
 * Douglas–Peucker simplification (`simplify-js`) with the tolerance given
 * the way the editor thinks about it: **screen pixels ÷ zoom**, converted to
 * percent of the plate width. The save path uses the contract's fixed
 * ε = 0.25 % (§4.0).
 */
import simplify from 'simplify-js'

import { dedupeRing } from './polygon'
import type { Poly, Ring } from './types'

/** Contract tolerance applied on save (§4.0: Douglas–Peucker ε = 0.25 %). */
export const SAVE_EPSILON_PCT = 0.25

/** Default on-the-fly tolerance while drawing (§9.3: auto-simplify 1.5 px ÷ k). */
export const DRAW_EPSILON_PX = 1.5

/**
 * Percent tolerance for a pixel tolerance at zoom `k`, given how many
 * screen pixels one percent of the plate width currently spans.
 */
export function pctTolerance(px: number, k: number, pxPerPct: number): number {
  if (!(k > 0) || !(pxPerPct > 0)) return 0
  return px / (k * pxPerPct)
}

/** Simplify a ring with a percent tolerance; never drops below 3 vertices. */
export function simplifyRing(ring: Ring, tolerancePct: number): Ring {
  const clean = dedupeRing(ring)
  if (clean.length <= 3 || tolerancePct <= 0) return clean
  // simplify-js treats the input as an open polyline, so the closing edge is
  // never considered. Append the first vertex, simplify, drop it again.
  const closed = [...clean, clean[0]!].map(([x, y]) => ({ x, y }))
  const out = simplify(closed, tolerancePct, true).map((p): [number, number] => [p.x, p.y])
  out.pop()
  const result = dedupeRing(out)
  return result.length >= 3 ? result : clean
}

export function simplifyPoly(poly: Poly, tolerancePct: number): Poly {
  const outer = simplifyRing(poly.outer, tolerancePct)
  if (!poly.holes || poly.holes.length === 0) return { outer }
  const holes = poly.holes.map((h) => simplifyRing(h, tolerancePct)).filter((h) => h.length >= 3)
  return holes.length ? { outer, holes } : { outer }
}

/** Simplify an open polyline (paths) with a percent tolerance; keeps ≥ 2 points. */
export function simplifyLine(points: Ring, tolerancePct: number): Ring {
  if (points.length <= 2 || tolerancePct <= 0) return points
  const out = simplify(
    points.map(([x, y]) => ({ x, y })),
    tolerancePct,
    true,
  ).map((p): [number, number] => [p.x, p.y])
  return out.length >= 2 ? out : points
}

/** What the autosave path applies before a PUT (§4.0). */
export function simplifyForSave(poly: Poly): Poly {
  return simplifyPoly(poly, SAVE_EPSILON_PCT)
}
